import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { ResearchLibraryClient } from './library/api/client';

interface UseAddPublicResearchToProjectOptions {
  onSuccess?: (projectId: string | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for adding public research from research_library to projects
 * Creates a copy in workbook_research table
 */
export const useAddPublicResearchToProject = (itemId: string, options: UseAddPublicResearchToProjectOptions = {}) => {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // State for tracking project assignments
  const [assignedProjects, setAssignedProjects] = useState<string[]>([]);
  const [optimisticProjectId, setOptimisticProjectId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Check if research is already in a specific project
  const checkProjectAssignment = useCallback(async (projectId: string): Promise<boolean> => {
    if (!user?.id) return false;
    
    try {
      return await ResearchLibraryClient.checkResearchInProject(itemId, user.id, projectId);
    } catch (error) {
      console.warn('Failed to check project assignment:', error);
      return false;
    }
  }, [itemId, user?.id]);

  // Mutation for copying research to project
  const copyToProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // ✅ REMOVED: checkProjectAssignment call that was causing 406 errors
      // Public research can be copied multiple times - duplicates are fine
      // The UI can handle "already copied" state through local tracking if needed

      await ResearchLibraryClient.copyToWorkbookResearch(itemId, user.id, projectId);
      return projectId;
    },
    onMutate: (projectId) => {
      // Optimistic update
      setOptimisticProjectId(projectId);
      setHasOptimisticUpdate(true);
      setAssignedProjects(prev => [...prev, projectId]);
    },
    onSuccess: (projectId) => {
      // Update actual state with server response
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      
      // Comprehensive cache invalidation to match private research patterns
      // Invalidate public research library caches
      queryClient.invalidateQueries({ queryKey: ['research-library-list'] });
      
      // FIXED: Invalidate workbook/project data caches with exact: false for filter variations
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-research'] });
      
      // FIXED: Invalidate BOTH Project Research Library AND Private Research Generator caches
      // Project Research Library (project-specific research)
      queryClient.invalidateQueries({ queryKey: ['private-research-generator'] }); // All projects
      queryClient.invalidateQueries({ queryKey: ['private-research-generator-count'] }); // All counts
      queryClient.invalidateQueries({ queryKey: ['private-research-generator', projectId] });
      queryClient.invalidateQueries({ queryKey: ['private-research-generator-count', projectId] });
      
      // Private Research Generator (user's private research from all sources)
      queryClient.invalidateQueries({ queryKey: ['private-research-library'] }); // All user private research
      queryClient.invalidateQueries({ queryKey: ['private-research-library-count'] }); // All user private research counts
      
      // Invalidate project-specific research queries
      queryClient.invalidateQueries({ queryKey: ['generated-research-details'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['private-research-item-details'] });
      
      // ✅ NEW: Invalidate goal-related queries (critical for junction table approach)
      queryClient.invalidateQueries({ queryKey: ['suggested-research'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['generated-research'], exact: false });
      
      // Invalidate research project assignment queries
      queryClient.invalidateQueries({ queryKey: ['research-project-assignment', itemId] });
      
      // Invalidate universal storage caches (since public research can appear there)
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      toast({
        title: "Added to Project",
        description: "Research successfully copied to project",
        variant: "default"
      });
      
      options.onSuccess?.(projectId);
    },
    onError: (error, projectId) => {
      // Revert optimistic update
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      setAssignedProjects(prev => prev.filter(id => id !== projectId));
      
      console.error('Failed to copy research to project:', error);
      toast({
        title: "Failed to Add to Project",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
      
      options.onError?.(error instanceof Error ? error : new Error('Unknown error'));
    }
  });

  // Check if research is in any of the tracked projects
  const isInAnyProject = useMemo(() => {
    return assignedProjects.length > 0 || hasOptimisticUpdate;
  }, [assignedProjects.length, hasOptimisticUpdate]);

  return {
    // Actions
    copyToProject: copyToProjectMutation.mutateAsync,
    checkProjectAssignment,
    
    // State
    assignedProjects,
    isInAnyProject,
    isPending: copyToProjectMutation.isPending,
    
    // Optimistic state (for UI feedback)
    optimisticProjectId,
    hasOptimisticUpdate
  };
}; 